import { themeVar } from '@/styles/theme';
import styled, { keyframes } from 'styled-components';

export const TableWrapper = styled.div<{ padding?: string }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: ${({ padding }) => padding ?? '10px 0 30px'};
`;

/** Outer container for tables with horizontal scroll + sticky header (no overflow) */
export const TableWithHorizontalScrollOuter = styled.div<{ isTablet: boolean }>`
  width: 100%;
  max-width: ${({ isTablet }) => (isTablet ? 'calc(100vw - 100px)' : 'calc(100vw - 180px)')};
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

/** Sticky header wrapper: use outside overflow container so position: sticky works */
export const StickyTableHeaderWrapper = styled.div<{ tableFiltersHeight: number; isTablet: boolean }>`
  position: sticky;
  top: ${({ tableFiltersHeight, isTablet }) => (isTablet ? `${50 + tableFiltersHeight}px` : `${tableFiltersHeight}px`)};
  z-index: 8;
  overflow-x: hidden;
  overflow-y: visible;
  background-color: ${themeVar('header.base')};
  border-radius: 8px;
`;

/** Clips sticky header content when syncing horizontal scroll via transform */
export const StickyTableHeaderClip = styled.div`
  overflow-x: hidden;
  width: 100%;
  border-radius: 8px;
`;

/** Inner wrapper for sticky header; set width to table total width, attach ref for transform */
export const StickyTableHeaderInner = styled.div<{ $width: number }>`
  width: ${({ $width }) => `${$width}px`};
  min-width: 100%;
`;

export const TableWithHorizontalScrollWrapper = styled.div<{ isTablet: boolean }>`
  width: 100%;
  max-width: ${({ isTablet }) => (isTablet ? 'calc(100vw - 100px)' : 'calc(100vw - 180px)')};
  overflow-x: auto;
  overflow-y: hidden;
  overscroll-behavior-x: none;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 6px;
  }
`;

export const TableWithHorizontalScroll = styled.div<{ totalWidth: string }>`
  width: ${({ totalWidth }) => totalWidth};
  min-width: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const TableBodyWrapper = styled.div<{ padding?: string }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const blinkingFrame = keyframes`
  0% {
    opacity: 1;
  }
  25% {
    opacity: 0.4;
  }
  50% {
    opacity: 1;
  }
  75% {
    opacity: 0.4;
  }
  100% {
    opacity: 1;
  }
`;

export const TableBodyWrapperWithLoading = styled(TableBodyWrapper)<{ isLoading: boolean }>`
  opacity: ${({ isLoading }) => (isLoading ? 0 : 1)};
  transition: opacity 0.2s ease;

  ${({ isLoading }) =>
    isLoading &&
    `
    & * {
      pointer-events: none !important;
      user-select: none !important;
    }
  `}
`;

export const TableRowWrapper = styled.div<{ addAdaptive?: boolean; loading?: boolean | number }>`
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 60px;
  background-color: ${themeVar('layer.1')};
  border-radius: 8px;
  padding: 0 24px;
  gap: 5px;
  opacity: ${({ loading }) => (loading ? 0.5 : 1)};
  pointer-events: ${({ loading }) => (loading ? 'none' : 'auto')};
  animation: ${({ loading }) => (loading ? blinkingFrame : null)} 1.5s infinite;

  @media (max-width: 1200px) {
    padding: 0 ${({ addAdaptive }) => (addAdaptive ? 10 : 24)}px;
  }

  & #actionDropdown {
    opacity: 0;
  }
  &:hover #actionDropdown {
    opacity: 1;
  }

  &:hover #infoWrap {
    opacity: 1;
  }

  &:hover #actionText {
    color: ${themeVar('font.action')};
  }

  &:hover #actionCurioText {
    color: ${themeVar('font.action')};
  }

  &:hover #actionOrangeText {
    color: #ef9208;
  }

  &:hover #tempInfoWrap {
    display: none;
  }

  &:hover #arrowInSelectRow {
    svg {
      path {
        fill: #ef9208;
      }
    }
  }

  &:hover #selector {
    outline: 1px solid ${themeVar('border.base')} !important;
    background-color: ${themeVar('layer.1')};
  }

  &:hover #selectIcon {
    opacity: 1;
  }

  &:hover {
    background-color: ${themeVar('layer.12')};
    transition: background-color 100ms ease;
  }
`;

export const TableWithHorizontalScrollRowWrapper = styled(TableRowWrapper)<{ alignItems?: 'flex-start' | 'center' }>`
  display: flex;
  align-items: ${({ alignItems }) => alignItems ?? 'stretch'};
  gap: 0;
  padding: 0;

  &:hover #fixedCell {
    background-color: ${themeVar('layer.12')};
    transition: background-color 100ms ease;
  }
`;

export const TableRowCell = styled.div<{ width?: number; withHorizontalScroll?: boolean }>`
  display: flex;
  justify-content: flex-start;
  width: ${({ width, withHorizontalScroll }) => (width === undefined ? 'auto' : withHorizontalScroll ? `${width}px` : `${width}%`)};
`;

export const TableScrollableCellsWrapper = styled.div<{ alignItems?: 'flex-start' | 'center' }>`
  display: flex;
  align-items: ${({ alignItems }) => alignItems ?? 'flex-start'};
  justify-content: space-between;
  gap: 10px;
  padding: 10px 24px;
  width: 100%;
`;

export const TableFixedCell = styled.div<{ width: number; alignItems?: 'flex-start' | 'center' }>`
  display: flex;
  align-items: ${({ alignItems }) => alignItems ?? 'flex-start'};
  padding: 0 24px;
  margin: 10px 0;
  position: sticky;
  left: 0;
  z-index: 1;
  max-width: ${({ width }) => `${width}px`};
  width: 100%;
  border-right: 1px solid ${themeVar('border.base')};
  border-radius: 8px 0 0 8px;
  background-color: ${themeVar('layer.1')};
`;

export const TitleCell = styled.span<{ color?: string }>`
  font-family: Blinker, serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
  letter-spacing: 0;
  text-align: left;
  color: ${themeVar('font.base')};
  @media (max-width: 1200px) {
    font-size: 15px;
  }
`;

export const TextCell = styled(TitleCell)<{ bold?: boolean }>`
  font-weight: ${({ bold }) => (bold ? 700 : 400)};
`;

export const CellWrapper = styled.div<{
  width?: string;
  flexDirection?: 'row' | 'column';
  alignItems?: 'flex-start' | 'center';
  justifyContent?: 'space-between';
  isMobile?: boolean;
}>`
  width: ${({ width }) => width ?? 'auto'};
  display: flex;
  align-items: ${({ alignItems }) => alignItems ?? 'flex-start'};
  flex-direction: ${({ flexDirection }) => flexDirection ?? 'row'};
  justify-content: ${({ justifyContent }) => justifyContent ?? 'flex-start'};
  gap: ${({ isMobile }) => isMobile && '8px'};
`;
